import { useTranslation } from 'react-i18next';
import MoreHorizRounded from '@mui/icons-material/MoreHorizRounded';
import IconButton from '@mui/material/IconButton';
import Stack from '@mui/material/Stack';
import { alpha } from '@mui/material/styles';
import Typography from '@mui/material/Typography';
import { GridCellParams, GridColDef, DataGrid as MuiDataGrid } from '@mui/x-data-grid';
import Constants from 'style/constants';
import OverflowMenu from 'shared/components/overflow-menu/overflow-menu';

import { DataGridProps, useDataGrid } from './hooks';

export type DataGridOverflowMenuItem<DataType> = {
  label: string;
  action?: (item: DataType) => void;
  disabled?: (item: DataType) => boolean;
  isVisible?: (item: DataType) => boolean;
};

const DataGrid = <DataType extends object>(props: Readonly<DataGridProps<DataType>>): React.JSX.Element => {
  const { data, rowModesModel, overflowMenuOptions, editButtons, columns: columnsProp } = props;
  const { t } = useTranslation('dataGrid');

  const { isOverflowMenuOpen, anchorEl, clickedRow, closeOverflowMenu, mapMenuItems, getActionCellState, onMoreClick } =
    useDataGrid<DataType>(props);

  const styling = {
    boxShadow: 0,
    border: 0,
    [`& .MuiDataGrid-cell:focus-within, & .MuiDataGrid-cell.MuiDataGrid-cell--editing:focus-within`]: {
      outline: 'none',
    },
    [`& .MuiDataGrid-columnHeader:focus, & .MuiDataGrid-columnHeader:focus-within`]: {
      outline: 'none',
    },
    [`& .MuiDataGrid-columnHeader`]: {
      padding: 2,
    },
    [`& .MuiDataGrid-cell.datagrid-cell`]: {
      padding: 2,
    },
    [`& .MuiDataGrid-row:hover`]: {
      backgroundColor: alpha(Constants.Colors.secondaryContainer, 0.16),
    },
    [`& .MuiDataGrid-columnHeaderTitle`]: {
      fontSize: '14px',
    },
    [`& .MuiDataGrid-columnSeparator`]: {
      display: 'none',
    },
  };

  const renderActionsCell = (params: GridCellParams): React.JSX.Element | null => {
    const { isEditMode, showOverflowMenu } = getActionCellState(params.row.id);
    if (isEditMode && editButtons) {
      return (
        <>
          {editButtons.map((btn) => (
            <span key={btn.key}>{btn}</span>
          ))}
        </>
      );
    }
    if (showOverflowMenu) {
      return (
        <IconButton
          className="more-icon-button"
          onClick={(event: React.MouseEvent<HTMLElement, MouseEvent>) => onMoreClick(event, params.row)}
        >
          <MoreHorizRounded className="more-icon" />
        </IconButton>
      );
    }
    return null;
  };

  const columns = (): GridColDef[] => {
    const columns = columnsProp.slice();
    if (overflowMenuOptions || editButtons) {
      columns.push({
        field: 'actions',
        type: 'actions',
        flex: 1,
        align: 'right',
        renderCell: renderActionsCell,
      });
    }

    return columns;
  };

  const renderNoItems = (): React.JSX.Element => (
    <Stack alignItems="center" padding={'12px 0px'} justifyContent="center">
      <Typography variant="subtitle1">{t('noItemsText')}</Typography>
    </Stack>
  );

  return (
    <>
      <MuiDataGrid
        rows={data}
        columns={columns()}
        rowModesModel={rowModesModel}
        editMode="row"
        sx={styling}
        sortingOrder={['asc', 'desc']}
        getCellClassName={(params: GridCellParams) => (params.field === 'actions' ? '' : 'datagrid-cell')}
        getRowHeight={() => 'auto'}
        disableColumnMenu
        disableColumnResize
        disableColumnSorting
        disableRowSelectionOnClick
        hideFooter={true}
        initialState={{
          sorting: {
            sortModel: [{ field: columns()[0].field, sort: 'asc' }],
          },
        }}
        slots={{
          noRowsOverlay: renderNoItems,
          noResultsOverlay: renderNoItems,
        }}
      />
      {overflowMenuOptions && clickedRow && (
        <OverflowMenu
          isOpen={isOverflowMenuOpen}
          onClose={closeOverflowMenu}
          anchorEl={anchorEl}
          menuItems={mapMenuItems(overflowMenuOptions, clickedRow)}
        />
      )}
    </>
  );
};

export default DataGrid;
